import React, { useState, useEffect } from "react";
import { Typography, Chip } from "@mui/material";
import CustomTable from "../../../components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import { useAllResourcesViewModel } from "./AllResourceViewModel";
import AddIcon from "@mui/icons-material/Add";
import { useLoading } from "../../../contexts/LoadingContext";
import Modal from "../../../components/Modal/Modal";
import AddResource from "../AddResource/AddResource";
import BusinessIcon from "@mui/icons-material/Business";
import CircleIcon from "@mui/icons-material/Circle";
import WorkIcon from "@mui/icons-material/Work";
import CategoryIcon from "@mui/icons-material/Category";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import WarningIcon from "@mui/icons-material/Warning";
import { getEnumLabel, getEnumOptions } from "../../../utils/enumUtils";
import ResourcesIcon from "@mui/icons-material/Groups";
import BusinessUnitIcon from "@mui/icons-material/AccountTree";
import CountriesIcon from "@mui/icons-material/Public";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import { useAllRolesViewModel } from "../../Admin/Roles/AllRolesViewModel";
import { useRbac } from "../../../hooks/RbacContext";

const AllResources = () => {
  const {
    resources,
    countries,
    businessUnits,
    reportingManagers,
    loading,
    error,
    getCountryName,
    getBusinessUnitName,
    getReportingManagerName,
    createResource,
    updateResource,
    handleDeleteResource,
    successModal,
    setSuccessModal,
  } = useAllResourcesViewModel();
  const { roles, getRoleName } = useAllRolesViewModel();

  const { showLoading, hideLoading } = useLoading();
  const [isAddResourceOpen, setIsAddResourceOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const { checkAccess } = useRbac();

  const columns = [
    {
      id: "first_name",
      label: "First Name",
    },
    {
      id: "last_name",
      label: "Last Name",
    },
    {
      id: "email",
      label: "Email",
      render: (value) => value || "N/A",
    },
    {
      id: "designation",
      label: "Designation",
    },
   {
    id: "department",
    label: "Department",
    render: (value) => value || "",
    searchValue: (value) => value || "",
   },
    {
      id: "reporting_manager",
      label: "Reporting Manager",
      render: (value) => getReportingManagerName(value) || "N/A",
      searchValue: (value) => getReportingManagerName(value),
    },
   
    {
      id: "employee_code",
      label: "Employee Code",
    },
    {
      id: "role_id",
      label: "Role",
      render: (value) => getRoleName(value),
    },
    {
      id: "is_active",
      label: "Status",
      align: "left",
      render: (value) => (
        <Chip
          label={value ? "Active" : "Inactive"}
          size="small"
          sx={{
            height: 24,
            fontSize: "0.75rem",
            fontWeight: 500,
            backgroundColor: value ? "#E8F5E9" : "#FFF3E0",
            color: value ? "#2E7D32" : "#EF6C00",
            "& .MuiChip-label": {
              px: 1.5,
            },
          }}
        />
      ),
      searchValue: (value) => (value ? "active" : "inactive"),
    },
    {
      id: "created_on",
      label: "Created On",
      render: (value) => {
        if (!value) return '-';
        const [day, month, year] = value.split('/');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        });
      }
    },
    {
      id: "updated_on",
      label: "Updated On",
      render: (value) => {
        if (!value) return '-';
        const [day, month, year] = value.split('/');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        });
      }
    },
  ];

  const fixedColumns = {
    left: [],
    right: ["type_of_service", "actions"],
  };

  const columnFilters = [
    {
      id: "username",
      type: "select",
      label: "Resource",
      icon: <ResourcesIcon />,
      sendIcon: true,
    },
    {
      id: "country_id",
      type: "select",
      label: "Country",
      render: (value) => getCountryName(value),
      icon: <CountriesIcon />,
      sendIcon: true,
    },
    {
      id: "type_of_service",
      type: "select",
      label: "Type Of Service",
      options: getEnumOptions("type_of_service"),
      icon: <CategoryIcon />,
      sendIcon: true,
    },
    {
      id: "business_unit",
      type: "select",
      label: "Business Unit",
      render: (value) => getBusinessUnitName(value),
      icon: <BusinessUnitIcon />,
      sendIcon: true,
    },
    {
      id: "is_active",
      label: "Status",
      align: "left",
      render: (value) => (value ? "Active" : "Inactive"),
      searchValue: (value) => (value ? "active" : "inactive"),
      icon: <CircleIcon />,
    },
  ];

  const handleAddResource = () => {
    setSelectedResource(null);
    setIsAddResourceOpen(true);
  };

  const handleEditResource = (resource) => {
    setSelectedResource(resource);
    setIsAddResourceOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddResourceOpen(false);
    setIsAddResourceOpen(null);
  };

  const handleSubmit = async (formData) => {
    try {
      showLoading();
      if (selectedResource) {
        await updateResource(selectedResource.resource_id, formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Resource updated successfully",
        });
      } else {
        await createResource(formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Resource added successfully",
        });
      }
      handleCloseDialog();
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "An unexpected error occurred",
      });
    } finally {
      hideLoading();
    }
  };

  const handleDeleteClick = (resource) => {
    setDeleteConfirmation({
      resource,
      open: true,
    });
  };

  const handleDeleteConfirm = async () => {
    const resource = deleteConfirmation.resource;
    try {
      showLoading();
      await handleDeleteResource(resource);
      setDeleteConfirmation(null);
      setSuccessModal({
        type: "success",
        title: "Success",
        message: "Resource deleted successfully",
      });
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "Failed to delete resource",
      });
    } finally {
      hideLoading();
    }
  };

  const getActions = (resource) => {
    const actions = [];
    if (checkAccess("resource", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: handleEditResource,
        color: "primary",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
          },
        },
      });
    }
    return actions;
  };

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }

  return (
    <div>
      <CustomTable
        columns={columns}
        data={resources}
        loading={loading}
        onAdd={handleAddResource}
        showActions={true}
        showAddButton={false}
        // addButtonIcon={<AddIcon />}
        showSearch={true}
        showColumnVisibility={false}
        pageSize={10}
        stickyHeader={true}
        fixedColumns={fixedColumns}
        actions={getActions}
        title="All Resources"
        subtitle="Manage and monitor all resources"
        addButtonText="Add Resource"
        alignment="left"
        fontSize="12px"
        showFilters={true}
        columnFilters={columnFilters}
      />

      <AddResource
        open={isAddResourceOpen}
        onClose={handleCloseDialog}
        initialData={selectedResource}
        onSubmit={handleSubmit}
        countries={countries}
        businessUnits={businessUnits}
        reportingManagers={reportingManagers}
        roles={roles.filter((e) => e.is_project === false)}
      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={() => setSuccessModal(null)}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title="Delete Resource"
          message={`Are you sure you want to delete resource "${deleteConfirmation.resource.username}"?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default AllResources;
